import { Box, Stack } from "@mui/material";
import {
  confirmPasswordReset,
  signInWithEmailAndPassword,
  signOut,
  verifyPasswordResetCode,
} from "firebase/auth";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "../../components/ui/Button";
import PasswordInput from "../../components/ui/PasswordInput";
import Text from "../../components/ui/Text";
import { ROUTES } from "../../constants/routes";
import { useToast } from "../../contexts/ToastContext";
import { auth } from "../../firebase/config";
import apiService from "../../services/api";
import { getFirebaseErrorMessage } from "../../utils/firebaseErrorMessages";
import { validatePassword } from "../../utils/validation";

const CreateNewPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSuccess, showError } = useToast();

  const oobCode = searchParams.get("oobCode");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({
    password: "",
    confirmPassword: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);

  // Verify the reset code and get the email
  useEffect(() => {
    const verifyCode = async () => {
      if (!oobCode) {
        showError("Invalid or missing reset code.");
        navigate(ROUTES.SIGNIN, { replace: true });
        return;
      }

      try {
        // Verify the password reset code and get the email
        const userEmail = await verifyPasswordResetCode(auth, oobCode);
        setEmail(userEmail);
        setIsVerifying(false);
      } catch (error) {
        const friendlyMessage = getFirebaseErrorMessage(error);
        showError(
          friendlyMessage || "Invalid or expired reset link. Please try again.",
        );
        navigate(ROUTES.SIGNIN, { replace: true });
      }
    };

    verifyCode();
  }, [oobCode, navigate, showError]);

  const handlePasswordChange = (event) => {
    const value = event.target.value;
    setPassword(value);

    // Clear error when user starts typing
    if (errors.password) {
      setErrors((prev) => ({ ...prev, password: "" }));
    }
  };

  const handleConfirmPasswordChange = (event) => {
    const value = event.target.value;
    setConfirmPassword(value);

    // Clear error when user starts typing
    if (errors.confirmPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate password
    const passwordError = validatePassword(password);
    if (passwordError) {
      newErrors.password = passwordError;
    }

    // Validate confirm password
    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!oobCode) {
      showError("Invalid reset code. Please try again.");
      return;
    }

    setIsSubmitting(true);

    try {
      // Confirm the password reset with Firebase
      await confirmPasswordReset(auth, oobCode, password);

      // Sign in with the new password to get a token
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );

      // Get the user token after sign in
      const token = await userCredential.user.getIdToken();
      await apiService.markUserNotMigrated(token);

      // Sign out immediately
      await signOut(auth);

      showSuccess("Password updated successfully!");

      // Redirect to signin page
      navigate(ROUTES.SIGNIN, {
        replace: true,
      });
    } catch (error) {
      const friendlyMessage = getFirebaseErrorMessage(error);
      showError(
        friendlyMessage ||
          "Failed to reset password. The link may have expired.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isVerifying) {
    return (
      <>
        <Box sx={{ mb: 3, textAlign: "center" }}>
          <Text variant="pageHeader" sx={{ mb: "8px !important" }}>
            Verifying...
          </Text>
          <Text
            variant="paragraph3"
            sx={{ fontSize: "16px", color: "#666666" }}
          >
            Please wait while we verify your reset link.
          </Text>
        </Box>
      </>
    );
  }

  return (
    <>
      {/* Welcome Section */}
      <Box sx={{ mb: 3 }}>
        <Text variant="pageHeader" sx={{ mb: "8px !important" }}>
          Create new password
        </Text>
        <Text
          variant="paragraph3"
          sx={{
            fontSize: "16px",
            color: "#666666",
          }}
        >
          Create a new password for{" "}
          <Text
            component="span"
            variant="paragraph3"
            sx={{
              fontWeight: 600,
              fontSize: "16px",
              color: "#000000",
            }}
          >
            {email}
          </Text>
        </Text>
      </Box>

      <form onSubmit={handleSubmit} noValidate>
        <Stack spacing={3}>
          {/* Password Input */}
          <PasswordInput
            label="Create a password"
            value={password}
            onChange={handlePasswordChange}
            error={!!errors.password}
            helperText={errors.password}
          />

          {/* Confirm Password Input */}
          <PasswordInput
            label="Confirm password"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
          />

          {/* Password Requirements */}
          <Text variant="paragraph3" sx={{ color: "#666666" }}>
            At least 8 characters, one upper and lower case letter, one number
            and one symbol.
          </Text>

          {/* Save Button */}
          <Button
            variant="primary"
            type="submit"
            disabled={isSubmitting}
            sx={{ mt: 3 }}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Stack>
      </form>
    </>
  );
};

export default CreateNewPassword;
